/**
 * Diagnostics / log upload store (GetDiagnostics 1.6 + GetLog 2.0.1).
 *
 * A browser-only simulator cannot actually upload to the CSMS-supplied target
 * (FTP is unreachable from a browser; most HTTP targets fail CORS). So the
 * lifecycle reports the REAL result (Uploaded only on a genuine HTTP success,
 * else UploadFailed) and ALSO makes the generated bundle downloadable locally,
 * so the tester gets the diagnostics file regardless. A manual "Mark Uploaded"
 * (see useStationStore.markDiagnosticsUploaded) lets the tester drive the CSMS
 * happy-path after placing the file by hand.
 *
 * The bundle text is held outside reactive state (module ref); the reactive
 * slice carries only metadata.
 */
import { create } from 'zustand';
import { sha256Hex } from '@/lib/sha256';

export type DiagnosticsStatus =
  | 'idle'
  | 'uploading'
  | 'uploaded'
  | 'failed';

/** 1.6 GetDiagnostics vs 2.0.1 GetLog — drives which status frame Mark-Uploaded sends. */
export type DiagnosticsKind = 'diagnostics' | 'log';

export interface DiagnosticsState {
  readonly status: DiagnosticsStatus;
  readonly kind: DiagnosticsKind | null;
  readonly location: string | null;
  readonly filename: string | null;
  readonly sizeBytes: number | null;
  readonly sha256: string | null;
  /** v2.0.1 GetLog requestId — needed to echo it on LogStatusNotification. */
  readonly requestId: number | null;
  readonly error: string | null;
  readonly at: number | null;
}

export interface DiagnosticsActions {
  /** Record a freshly-generated bundle and enter the uploading state. */
  record(input: {
    readonly kind: DiagnosticsKind;
    readonly location: string;
    readonly filename: string;
    readonly text: string;
    readonly requestId?: number;
  }): void;
  setUploaded(): void;
  setFailed(error: string): void;
  saveToDisk(): void;
  clear(): void;
}

export type DiagnosticsStore = DiagnosticsState & DiagnosticsActions;

// Bundle text held outside reactive state — never a render input.
let heldText: string | null = null;

const initial: DiagnosticsState = {
  status: 'idle',
  kind: null,
  location: null,
  filename: null,
  sizeBytes: null,
  sha256: null,
  requestId: null,
  error: null,
  at: null,
};

export const useDiagnosticsStore = create<DiagnosticsStore>((set, get) => ({
  ...initial,

  record(input): void {
    heldText = input.text;
    const bytes = new TextEncoder().encode(input.text);
    set({
      status: 'uploading',
      kind: input.kind,
      location: input.location,
      filename: input.filename,
      sizeBytes: bytes.byteLength,
      sha256: sha256Hex(input.text),
      requestId: input.requestId ?? null,
      error: null,
      at: Date.now(),
    });
  },

  setUploaded(): void {
    set({ status: 'uploaded', error: null });
  },

  setFailed(error): void {
    set({ status: 'failed', error });
  },

  saveToDisk(): void {
    if (heldText === null || typeof document === 'undefined') return;
    const { filename } = get();
    const blob = new Blob([heldText], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename ?? 'diagnostics.json';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },

  clear(): void {
    heldText = null;
    set(initial);
  },
}));

/** Whether a generated bundle is currently held in memory (non-reactive). */
export function hasHeldDiagnostics(): boolean {
  return heldText !== null;
}
